import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CropIcon from '@mui/icons-material/Crop';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

const PDF_JS_SCRIPT = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDF_JS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

function carregarPdfJs() {
  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER;
    return Promise.resolve(window.pdfjsLib);
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDF_JS_SCRIPT;
    script.async = true;
    script.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error('pdf.js não carregou.'));
        return;
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('Falha ao carregar pdf.js.'));
    document.head.appendChild(script);
  });
}

async function renderPageToDataUrl(pdf, pageNumber, scale = 0.35, rotation = 0) {
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale, rotation });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL('image/jpeg', 0.85);
}

function normalizeEditorFiles(files, file) {
  if (Array.isArray(files) && files.length) {
    return files
      .map((item) => {
        if (!item) return null;
        if (item.file instanceof File) {
          return {
            id: String(item.id || `${item.file.name}-${item.file.size}`),
            file: item.file,
            name: item.file.name || item.name || 'PDF',
          };
        }
        if (item instanceof File) {
          return {
            id: `${item.name}-${item.size}-${item.lastModified}`,
            file: item,
            name: item.name,
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  if (file instanceof File) {
    return [{ id: 'single', file, name: file.name }];
  }
  return [];
}

function shortFileName(name, max = 18) {
  const raw = String(name || 'PDF');
  if (raw.length <= max) return raw;
  const ext = raw.includes('.') ? raw.slice(raw.lastIndexOf('.')) : '';
  const base = raw.slice(0, Math.max(4, max - ext.length - 1));
  return `${base}…${ext}`;
}

/**
 * Editor reutilizável de páginas PDF (miniaturas + rotação/crop/excluir/reordenar).
 * Aceita um arquivo (`file`) ou vários (`files`: [{ id, file }]).
 * Novos PDFs entram no fim da sequência, sem resetar páginas já editadas.
 * @param {{ file?: File, files?: Array<{id:string,file:File}>, disabled?: boolean, onApply: (pagesSpec: Array) => void|Promise<void>, applyLabel?: string }} props
 */
export default function PageEditor({ file, files, disabled = false, onApply, applyLabel = 'Aplicar e salvar' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pages, setPages] = useState([]); // { id, fileId, fileName, sourceIndex, rotation, crop, thumb, selected }
  const [dragIndex, setDragIndex] = useState(null);
  const [cropTarget, setCropTarget] = useState(null); // page id
  const [cropPreviewUrl, setCropPreviewUrl] = useState('');
  const [cropRect, setCropRect] = useState(null); // {x,y,w,h} relative 0-1 during drag
  const [cropDraft, setCropDraft] = useState(null);
  const cropImgRef = useRef(null);
  const dragStartRef = useRef(null);
  const pdfsRef = useRef(new Map()); // fileId -> pdf.js doc
  const pagesRef = useRef([]);
  const fileList = useMemo(() => normalizeEditorFiles(files, file), [files, file]);
  const fileListRef = useRef(fileList);
  fileListRef.current = fileList;
  const filesKey = useMemo(() => fileList.map((f) => f.id).join('|'), [fileList]);

  const selectedCount = useMemo(() => pages.filter((p) => p.selected).length, [pages]);

  const rebuildThumbs = useCallback(async (list) => {
    const next = [];
    for (const p of list) {
      const pdf = pdfsRef.current.get(p.fileId);
      if (!pdf) {
        next.push({ ...p, thumb: p.thumb || '' });
        continue;
      }
      try {
        const thumb = await renderPageToDataUrl(pdf, p.sourceIndex + 1, 0.32, p.rotation || 0);
        next.push({ ...p, thumb });
      } catch {
        next.push({ ...p, thumb: p.thumb || '' });
      }
    }
    return next;
  }, []);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  useEffect(() => {
    let cancelled = false;
    async function syncFiles() {
      const list = fileListRef.current;
      const nextIds = new Set(list.map((f) => f.id));

      for (const id of [...pdfsRef.current.keys()]) {
        if (!nextIds.has(id)) pdfsRef.current.delete(id);
      }

      if (!list.length) {
        setPages([]);
        return;
      }

      const toLoad = list.filter((f) => !pdfsRef.current.has(f.id));
      if (toLoad.length) {
        setLoading(true);
        setError('');
      }
      try {
        if (toLoad.length) {
          const pdfjs = await carregarPdfJs();
          for (const item of toLoad) {
            const data = new Uint8Array(await item.file.arrayBuffer());
            const pdf = await pdfjs.getDocument({ data }).promise;
            if (cancelled) return;
            pdfsRef.current.set(item.id, pdf);
          }
        }
        if (cancelled) return;

        const have = new Set(
          pagesRef.current.filter((p) => nextIds.has(p.fileId)).map((p) => p.fileId)
        );
        const extra = [];
        for (const item of list) {
          if (have.has(item.id)) continue;
          const pdf = pdfsRef.current.get(item.id);
          if (!pdf) continue;
          for (let i = 0; i < pdf.numPages; i += 1) {
            extra.push({
              id: `p-${item.id}-${i}-${Date.now()}`,
              fileId: item.id,
              fileName: item.name,
              sourceIndex: i,
              rotation: 0,
              crop: null,
              thumb: '',
              selected: false,
            });
          }
        }

        setPages((prev) => {
          const kept = prev.filter((p) => nextIds.has(p.fileId));
          if (!extra.length) return kept;
          return [...kept, ...extra];
        });

        if (extra.length) {
          const withThumbs = await rebuildThumbs(extra);
          if (cancelled) return;
          const byId = new Map(withThumbs.map((p) => [p.id, p]));
          setPages((prev) => prev.map((p) => byId.get(p.id) || p));
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Erro ao carregar PDF para edição.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    syncFiles();
    return () => {
      cancelled = true;
    };
  }, [filesKey, rebuildThumbs]);

  const moveItem = (from, to) => {
    if (from == null || to == null || from === to) return;
    setPages((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const handleRotate = async (id) => {
    setPages((prev) => {
      const next = prev.map((p) => {
        if (p.id !== id) return p;
        return { ...p, rotation: ((p.rotation || 0) + 90) % 360 };
      });
      const target = next.find((p) => p.id === id);
      const pdf = target ? pdfsRef.current.get(target.fileId) : null;
      if (pdf && target) {
        renderPageToDataUrl(pdf, target.sourceIndex + 1, 0.32, target.rotation).then((thumb) => {
          setPages((cur) => cur.map((p) => (p.id === id ? { ...p, thumb } : p)));
        });
      }
      return next;
    });
  };

  const deleteSelected = () => {
    setPages((prev) => {
      const next = prev.filter((p) => !p.selected);
      if (!next.length) {
        setError('É necessário manter ao menos uma página.');
        return prev;
      }
      return next;
    });
  };

  const deleteOne = (id) => {
    setPages((prev) => {
      if (prev.length <= 1) {
        setError('É necessário manter ao menos uma página.');
        return prev;
      }
      return prev.filter((p) => p.id !== id);
    });
  };

  const toggleSelect = (id) => {
    setPages((prev) => prev.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p)));
  };

  const openCrop = async (page) => {
    const pdf = pdfsRef.current.get(page.fileId);
    if (!pdf) return;
    setCropTarget(page.id);
    setCropRect(null);
    setCropDraft(page.crop || null);
    setCropPreviewUrl('');
    try {
      const url = await renderPageToDataUrl(pdf, page.sourceIndex + 1, 1.2, page.rotation || 0);
      setCropPreviewUrl(url);
    } catch (e) {
      setError(e.message || 'Erro ao abrir recorte.');
      setCropTarget(null);
    }
  };

  const closeCrop = () => {
    setCropTarget(null);
    setCropPreviewUrl('');
    setCropRect(null);
    setCropDraft(null);
    dragStartRef.current = null;
  };

  const applyCrop = () => {
    if (!cropTarget) return;
    const rect = cropDraft;
    setPages((prev) =>
      prev.map((p) => (p.id === cropTarget ? { ...p, crop: rect } : p))
    );
    closeCrop();
  };

  const relPoint = (e) => {
    const el = cropImgRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };

  const handleApply = async () => {
    setError('');
    if (!pages.length) {
      setError('Nenhuma página para salvar.');
      return;
    }
    const pagesSpec = [];
    for (const p of pages) {
      const fileIndex = fileList.findIndex((f) => f.id === p.fileId);
      if (fileIndex < 0) {
        setError('Há páginas de um arquivo que foi removido. Recarregue e tente de novo.');
        return;
      }
      pagesSpec.push({
        fileIndex,
        sourceIndex: p.sourceIndex,
        rotation: p.rotation || 0,
        ...(p.crop ? { crop: p.crop } : {}),
      });
    }
    await onApply?.(pagesSpec);
  };

  return (
    <Box>
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Carregando miniaturas…
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!loading && pages.length > 0 && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              disabled={disabled || selectedCount === 0}
              onClick={deleteSelected}
            >
              Excluir selecionadas ({selectedCount})
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Arraste ≡ para reordenar · clique no checkbox para selecionar
              {fileList.length > 1
                ? ` · ${fileList.length} PDFs misturados no mesmo documento`
                : ''}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 1.5,
            }}
          >
            {pages.map((page, index) => (
              <Paper
                key={page.id}
                elevation={0}
                draggable={!disabled}
                onDragStart={() => setDragIndex(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  moveItem(dragIndex, index);
                  setDragIndex(null);
                }}
                onDragEnd={() => setDragIndex(null)}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: page.selected ? '#1976d2' : 'rgba(25,118,210,0.18)',
                  bgcolor: page.selected ? 'rgba(25,118,210,0.06)' : '#fff',
                  opacity: dragIndex === index ? 0.7 : 1,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <DragIndicatorIcon fontSize="small" color="action" sx={{ cursor: 'grab' }} />
                    <Typography variant="caption" fontWeight={700}>
                      {index + 1}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap title={page.fileName}>
                      {fileList.length > 1
                        ? `${shortFileName(page.fileName)} · p.${page.sourceIndex + 1}`
                        : `(orig. ${page.sourceIndex + 1})`}
                    </Typography>
                  </Stack>
                  <Checkbox
                    size="small"
                    checked={!!page.selected}
                    disabled={disabled}
                    onChange={() => toggleSelect(page.id)}
                    inputProps={{ 'aria-label': `Selecionar página ${index + 1}` }}
                  />
                </Stack>

                <Box
                  sx={{
                    position: 'relative',
                    bgcolor: '#f1f5f9',
                    borderRadius: 1,
                    overflow: 'hidden',
                    aspectRatio: '3 / 4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {page.thumb ? (
                    <Box
                      component="img"
                      src={page.thumb}
                      alt={`Página ${index + 1}`}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        transform: page.crop ? 'none' : undefined,
                        outline: page.crop ? '2px solid #ef6c00' : 'none',
                      }}
                    />
                  ) : (
                    <CircularProgress size={18} />
                  )}
                  {page.crop && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        left: 4,
                        bottom: 4,
                        bgcolor: 'rgba(239,108,0,0.9)',
                        color: '#fff',
                        px: 0.75,
                        borderRadius: 1,
                        fontWeight: 700,
                      }}
                    >
                      Recorte
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" justifyContent="center" spacing={0.25} sx={{ mt: 0.75 }}>
                  <Tooltip title="Girar 90°">
                    <span>
                      <IconButton size="small" disabled={disabled} onClick={() => handleRotate(page.id)}>
                        <RotateRightIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Recortar">
                    <span>
                      <IconButton size="small" disabled={disabled} onClick={() => openCrop(page)}>
                        <CropIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <span>
                      <IconButton size="small" disabled={disabled} onClick={() => deleteOne(page.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Paper>
            ))}
          </Box>

          <Button
            variant="contained"
            size="large"
            startIcon={disabled ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
            disabled={disabled || !pages.length}
            onClick={handleApply}
            sx={{ mt: 3 }}
          >
            {applyLabel}
          </Button>
        </>
      )}

      <Dialog open={Boolean(cropTarget)} onClose={closeCrop} maxWidth="md" fullWidth>
        <DialogTitle>Recortar página</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Arraste sobre a imagem para selecionar a área. O recorte é aplicado no PDF final.
          </Typography>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              maxWidth: '100%',
              userSelect: 'none',
              border: '1px solid #cbd5e1',
              borderRadius: 1,
              overflow: 'hidden',
            }}
            onMouseDown={(e) => {
              const pt = relPoint(e);
              dragStartRef.current = pt;
              setCropRect({ x: pt.x, y: pt.y, w: 0, h: 0 });
            }}
            onMouseMove={(e) => {
              if (!dragStartRef.current) return;
              const pt = relPoint(e);
              const x0 = dragStartRef.current.x;
              const y0 = dragStartRef.current.y;
              const x = Math.min(x0, pt.x);
              const y = Math.min(y0, pt.y);
              const w = Math.abs(pt.x - x0);
              const h = Math.abs(pt.y - y0);
              setCropRect({ x, y, w, h });
            }}
            onMouseUp={() => {
              if (cropRect && cropRect.w > 0.02 && cropRect.h > 0.02) {
                setCropDraft({
                  x: cropRect.x,
                  y: cropRect.y,
                  width: cropRect.w,
                  height: cropRect.h,
                });
              }
              dragStartRef.current = null;
              setCropRect(null);
            }}
            onMouseLeave={() => {
              if (dragStartRef.current) {
                dragStartRef.current = null;
              }
            }}
          >
            {cropPreviewUrl ? (
              <Box
                component="img"
                ref={cropImgRef}
                src={cropPreviewUrl}
                alt="Prévia para recorte"
                sx={{ display: 'block', maxWidth: '100%', maxHeight: '70vh' }}
                draggable={false}
              />
            ) : (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {(cropRect || cropDraft) && (() => {
              const r = cropRect
                ? { x: cropRect.x, y: cropRect.y, w: cropRect.w, h: cropRect.h }
                : {
                    x: cropDraft.x,
                    y: cropDraft.y,
                    w: cropDraft.width,
                    h: cropDraft.height,
                  };
              return (
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${r.x * 100}%`,
                    top: `${r.y * 100}%`,
                    width: `${r.w * 100}%`,
                    height: `${r.h * 100}%`,
                    border: '2px solid #1976d2',
                    bgcolor: 'rgba(25,118,210,0.15)',
                    pointerEvents: 'none',
                  }}
                />
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCropDraft(null)}>Limpar recorte</Button>
          <Button onClick={closeCrop}>Cancelar</Button>
          <Button variant="contained" onClick={applyCrop} disabled={!cropDraft}>
            Aplicar recorte
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
